// Time helpers for the canonical model. All domain time maths goes through Luxon
// in Europe/London (CLAUDE.md). Wall-clock times are converted to UTC instants once,
// at import, and every duration is computed between instants, never by subtracting
// wall-clock times. That's what makes shifts across midnight and clock changes come
// out right: a 22:00–06:00 shift is 7h on the spring change night and 9h in the autumn.

import { DateTime } from 'luxon';
import type { IsoDate, IsoInstant, LocalTime } from './types';

export const LONDON = 'Europe/London';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Rotas repeat the same few dates and times on every row, and Luxon zone maths is
// comparatively slow, so the pure conversions below are memoised. Results are
// immutable (strings, frozen objects or Luxon DateTimes), so sharing them is safe.
const CACHE_LIMIT = 50_000;
function memoise<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const cache = new Map<string, R>();
  return (...args: A) => {
    const key = args.join('|');
    const hit = cache.get(key);
    if (hit !== undefined || cache.has(key)) return hit as R;
    if (cache.size >= CACHE_LIMIT) cache.clear();
    const value = fn(...args);
    cache.set(key, value);
    return value;
  };
}
const LOCAL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * - `nonexistent`: the wall time falls in the spring-forward gap (e.g. 01:30 on the
 *   last Sunday in March). It's moved forward by the gap, so 01:30 becomes 02:30 BST.
 * - `ambiguous`: the wall time happens twice in the autumn fall-back (e.g. 01:30 on
 *   the last Sunday in October). The earlier occurrence (BST) is used.
 *
 * Either way the importer should raise a warning so the operator can check the row.
 */
export type LocalTimeNote = 'nonexistent' | 'ambiguous';

export type LocalTimeResult =
  | { ok: true; instant: IsoInstant; note?: LocalTimeNote }
  | { ok: false; reason: 'invalid-date' | 'invalid-time' };

/** Converts a local date and "HH:mm" wall time in `zone` to a UTC instant. */
export function localToInstant(
  date: IsoDate,
  time: LocalTime,
  zone: string = LONDON,
): LocalTimeResult {
  return cachedLocalToInstant(date, time, zone);
}

const cachedLocalToInstant = memoise((date: IsoDate, time: LocalTime, zone: string) =>
  Object.freeze(computeLocalToInstant(date, time, zone)),
);

function computeLocalToInstant(date: IsoDate, time: LocalTime, zone: string): LocalTimeResult {
  const d = ISO_DATE.exec(date);
  if (!d) return { ok: false, reason: 'invalid-date' };
  const t = LOCAL_TIME.exec(time);
  if (!t) return { ok: false, reason: 'invalid-time' };

  const [year, month, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [hour, minute] = [Number(t[1]), Number(t[2])];
  const date0 = DateTime.fromObject({ year, month, day }, { zone: 'utc' });
  // Luxon rolls invalid dates such as 31 February forward instead of rejecting them.
  if (!date0.isValid || date0.month !== month || date0.day !== day) {
    return { ok: false, reason: 'invalid-date' };
  }

  // Worked out from the zone's offsets either side of the wall time, not with
  // DateTime.fromObject: Luxon seeds its offset guess from the current time, so a time in
  // the repeated or skipped hour would resolve differently depending on when the check runs.
  const wall = DateTime.utc(year, month, day, hour, minute).toMillis();
  const offsetAt = (ms: number) => DateTime.fromMillis(ms, { zone }).offset * 60_000;
  const before = offsetAt(wall - 12 * 3_600_000);
  const after = offsetAt(wall + 12 * 3_600_000);
  const valid = [...new Set([before, after])]
    .map((offset) => wall - offset)
    .filter((ms) => wall - ms === offsetAt(ms))
    .sort((a, b) => a - b);
  const instantAt = (ms: number) => toIso(DateTime.fromMillis(ms, { zone }));

  const [earliest] = valid;
  // Skipped by the spring change: keep the pre-change offset, which moves it forward by the gap.
  if (earliest === undefined) {
    return { ok: true, instant: instantAt(wall - before), note: 'nonexistent' };
  }
  // Repeated by the autumn change: use the earlier occurrence (BST).
  if (valid.length > 1) return { ok: true, instant: instantAt(earliest), note: 'ambiguous' };
  return { ok: true, instant: instantAt(earliest) };
}

export type ShiftInstantsResult =
  | {
      ok: true;
      start: IsoInstant;
      end: IsoInstant;
      /** True when the end time was on or before the start time, so it was moved to the next day. */
      overnight: boolean;
      startNote?: LocalTimeNote;
      endNote?: LocalTimeNote;
    }
  | { ok: false; reason: 'invalid-date' | 'invalid-time' | 'zero-length' };

/**
 * Converts a rota row's date plus start and end times into instants. An end time
 * earlier than the start time means the shift crosses midnight and ends the next day.
 * An end time equal to the start time is rejected as a zero-length shift: a 24-hour
 * shift is far more likely to be a data error, and it's flagged rather than guessed.
 */
export function shiftInstants(
  date: IsoDate,
  startTime: LocalTime,
  endTime: LocalTime,
  zone: string = LONDON,
): ShiftInstantsResult {
  const start = localToInstant(date, startTime, zone);
  if (!start.ok) return start;
  if (!LOCAL_TIME.test(endTime)) return { ok: false, reason: 'invalid-time' };
  if (endTime === startTime) return { ok: false, reason: 'zero-length' };

  // "HH:mm" strings compare correctly as text.
  const overnight = endTime < startTime;
  const end = localToInstant(overnight ? addDays(date, 1) : date, endTime, zone);
  if (!end.ok) return end;

  return {
    ok: true,
    start: start.instant,
    end: end.instant,
    overnight,
    ...(start.note && { startNote: start.note }),
    ...(end.note && { endNote: end.note }),
  };
}

/** Parses an ISO instant. Returns undefined if it isn't a valid instant with an offset. */
export const parseInstant: (instant: IsoInstant) => DateTime | undefined = memoise(
  (instant: IsoInstant) => {
    const dt = DateTime.fromISO(instant, { setZone: true });
    if (!dt.isValid || !/(Z|[+-]\d{2}:?\d{2})$/.test(instant)) return undefined;
    return dt.toUTC();
  },
);

/** Milliseconds since the Unix epoch, for fast sorting and comparison of instants. */
export function instantMillis(instant: IsoInstant): number {
  return mustParse(instant).toMillis();
}

/** Elapsed minutes between two instants (real time, so DST-safe). */
export function durationMinutes(start: IsoInstant, end: IsoInstant): number {
  return (instantMillis(end) - instantMillis(start)) / 60_000;
}

/** The instant expressed in local time (default Europe/London), for display and calendar logic. */
export function toLocal(instant: IsoInstant, zone: string = LONDON): DateTime {
  return mustParse(instant).setZone(zone);
}

/** The local calendar date (default Europe/London) on which an instant falls. */
export const localDate: (instant: IsoInstant, zone?: string) => IsoDate = (
  instant,
  zone = LONDON,
) => cachedLocalDate(instant, zone);

const cachedLocalDate = memoise(
  (instant: IsoInstant, zone: string) => toLocal(instant, zone).toISODate() as IsoDate,
);

/** Adds whole calendar days to an ISO date. */
export const addDays: (date: IsoDate, days: number) => IsoDate = memoise(
  (date: IsoDate, days: number) =>
    DateTime.fromISO(date, { zone: 'UTC' }).plus({ days }).toISODate() as IsoDate,
);

/**
 * Age in whole years on a calendar date. Returns undefined for invalid input or a
 * date before the date of birth.
 *
 * Someone born on 29 February is treated as reaching each new age on 1 March in
 * non-leap years. That's the conservative choice for compliance (young-worker rules
 * apply a day longer). Luxon's default would use 28 February. Open question OQ30
 * in docs/rules.md.
 */
export function ageOnDate(dateOfBirth: IsoDate, date: IsoDate): number | undefined {
  const dob = DateTime.fromISO(dateOfBirth, { zone: 'UTC' });
  const on = DateTime.fromISO(date, { zone: 'UTC' });
  if (!ISO_DATE.test(dateOfBirth) || !ISO_DATE.test(date) || !dob.isValid || !on.isValid) {
    return undefined;
  }
  if (on < dob) return undefined;

  let age = on.year - dob.year;
  const birthdayThisYear = (() => {
    const candidate = dob.set({ year: on.year });
    // Luxon clamps 29 Feb to 28 Feb in non-leap years; use 1 March instead.
    return candidate.day !== dob.day ? candidate.plus({ days: 1 }) : candidate;
  })();
  if (on < birthdayThisYear) age -= 1;
  return age;
}

function mustParse(instant: IsoInstant): DateTime {
  const dt = parseInstant(instant);
  if (!dt) throw new Error(`Invalid instant: ${instant}`);
  return dt;
}

function toIso(dt: DateTime): IsoInstant {
  return dt.toUTC().toISO() as IsoInstant;
}
